<template>
  <div>
    <MatchPairSlideOption
      :key="slideOption.order"
      v-for="(slideOption, index) in slideOptions"
      :index="index"
      :slideOption="slideOption"
      :slideActive="slideActive"
      :presentation="presentation"
      :is-deletable="slideOptions.length > 1"
      :handleDeleteSlideOption="handleDeleteSlideOption"
      :handleSlideOptionChanged="handleSlideOptionChanged"
    />

    <aha-antd-button
      v-if="slideOptions.length < 4"
      v-aha-emit-action
      v-blur-on-click
      name="addMatchPair"
      variant="secondary"
      iconName="add"
      iconViewBox="0 0 24 24"
      class="w-full add-pair-btn"
      @click="addPair"
    >
      <span>
        {{ $i18nPresenter.t('Add Pair') }}
      </span>
    </aha-antd-button>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import EventBus from '@/eventBus'

// api
import SlideOptionAPI from '@/api/slideOption'

// components
import MatchPairSlideOption from '@/components-v2/presentation-editor/side-right/MatchPairSlideOption.vue'

export default {
  components: {
    MatchPairSlideOption,
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      slideOptions: (state) => state.slideActive.SlideOptions,
    }),
  },
  async mounted() {
    if (this.slideOptions.length === 0) {
      const newSlideOptions = [
        {
          title: '',
          pairTitle: '',
          order: 1,
        },
        {
          title: '',
          pairTitle: '',
          order: 2,
        },
        {
          title: '',
          pairTitle: '',
          order: 3,
        },
        {
          title: '',
          pairTitle: '',
          order: 4,
        },
      ]

      this.updateSlideActiveAction({ SlideOptions: newSlideOptions })

      const promises = newSlideOptions.map((item) => SlideOptionAPI.createSlideOption({
        slideId: this.slideActive.id,
        presentationId: this.presentation.id,
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        ...item,
      }))

      try {
        this.setSyncLoadingAction(1)
        const result = await Promise.all(promises)
        this.updateSlideActiveAction({ SlideOptions: result })
        this.setSyncLoadingAction(2)
      } catch (e) {
        Sentry.captureException(e)
        this.setSyncLoadingAction(3)
      }
      EventBus.$emit('match-pairs-change')
    }
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    async addPair() {
      if (this.slideOptions.length >= 4) return

      const maxOrder = this.slideOptions.reduce((maxNumber, slideOption) => {
        return slideOption.order > maxNumber ? slideOption.order : maxNumber
      }, 0)

      try {
        this.setSyncLoadingAction(1)
        const newSlideOption = {
          title: '',
          pairTitle: '',
          order: maxOrder + 1,
        }

        this.updateSlideActiveAction({ SlideOptions: [...this.slideOptions, newSlideOption] })

        const slideOption = await SlideOptionAPI.createSlideOption({
          slideId: this.slideActive.id,
          presentationId: this.presentation.id,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
          ...newSlideOption,
        })

        const newSlideOptions = this.slideOptions.map((item) => {
          return item.order === newSlideOption.order ? slideOption : item
        })

        this.updateSlideActiveAction({ SlideOptions: newSlideOptions })
        this.setSyncLoadingAction(2)
      } catch (e) {
        Sentry.captureException(e)
        this.setSyncLoadingAction(3)
      }
    },
    async handleDeleteSlideOption(slideOptionId) {
      const newSlideOptions = this.slideOptions
        .filter((item) => item.id !== slideOptionId)

      this.updateSlideActiveAction({ SlideOptions: newSlideOptions })

      try {
        this.setSyncLoadingAction(1)
        await SlideOptionAPI.deleteSlideOption(slideOptionId, {
          presentationId: this.presentation.id,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
        })
        this.setSyncLoadingAction(2)
        EventBus.$emit('match-pairs-change')
      } catch (e) {
        Sentry.captureException(e)
        this.setSyncLoadingAction(3)
      }
    },
    async handleSlideOptionChanged(slideOptionId, payload) {
      const slideId = this.slideActive.id
      try {
        this.setSyncLoadingAction(1)
        const newSlideOption = await SlideOptionAPI.patchSlideOptionById(slideOptionId, {
          slideId,
          presentationId: this.presentation.id,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
          ...payload,
        })
        let newSlideOptions = []
        if (slideId === this.slideActive.id) {
          newSlideOptions = this.slideOptions.map((item) => {
            if (item.id === slideOptionId) {
              return {
                ...item,
                ...newSlideOption,
                image: item.image,
                imageCropped: item.imageCropped,
                pairImage: item.pairImage,
                pairImageCropped: item.pairImageCropped,
              }
            }
            return item
          })

          this.updateSlideActiveAction({ SlideOptions: newSlideOptions })
        } else {
          const indexSlide = this.presentation.slides.findIndex((item) => item.id === slideId)
          if (indexSlide !== -1) {
            newSlideOptions = this.presentation.slides[indexSlide].SlideOptions.map((item) => {
              if (item.id === slideOptionId) {
                return { ...item, ...newSlideOption }
              }
              return item
            })
            this.$store.state.presentation.slides[indexSlide].SlideOptions = newSlideOptions
          }
        }
        this.setSyncLoadingAction(2)
        EventBus.$emit('match-pairs-change')
      } catch (e) {
        Sentry.captureException(e)
        this.setSyncLoadingAction(3)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.add-pair-btn {
  @apply flex items-center justify-center w-full;
}
</style>
